import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { catchError, finalize, throwError } from 'rxjs';
import { PhoneReport, PhoneReportOutcomeOutput, PhoneReportOutcomeResolutionEnum } from '../../../shared/models/phone-report.model';
import { DialogRef } from '../../../shared/services/modal.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { PhoneReportConnectivityUtils } from '../../../shared/services/phone-report-connectivity-utils';
import { PhoneReportUtils } from '../../../shared/services/phone-report-utils';
import { PhoneReportService } from '../../../shared/services/phone-report.service';
import { ValidationService, Validators } from '../../../shared/services/validation.service';

@Component({
  standalone: false,
  selector: 'app-phone-reports-report-outcome-modal',
  templateUrl: './phone-reports-report-outcome-modal.component.html',
  styleUrls: ['./phone-reports-report-outcome-modal.component.scss'],
})
export class PhoneReportReportOutcomeModalComponent {
  readonly dialogRef = input<DialogRef>();
  readonly phoneReport = input<PhoneReport>();
  readonly isAdmin = input<boolean>(false);

  readonly phoneReportService = inject(PhoneReportService);
  readonly notificationService = inject(NotificationService);
  readonly formBuilder = inject(FormBuilder);
  readonly validationService = inject(ValidationService);

  // output
  readonly reportOutcomeUpdate = output<PhoneReportOutcomeOutput>();
  readonly modalClose = output<void>();

  readonly isReportOutcomeUpdating = signal<boolean>(false);

  // computed
  readonly connectivityInfo = computed(() => {
    const info = PhoneReportConnectivityUtils.computeConnectivityInfo(this.phoneReport());

    return { report: this.phoneReport(), connectivityInfo: info, isAdmin: this.isAdmin() };
  });

  readonly outcomeResolutionOptions: { value: PhoneReportOutcomeResolutionEnum; label: string }[] = [
    { value: PhoneReportOutcomeResolutionEnum.UNDER_REVIEW, label: 'Under Review' },
    { value: PhoneReportOutcomeResolutionEnum.EXTERNAL_NUMBER, label: 'Sender Issue' },
    { value: PhoneReportOutcomeResolutionEnum.GHOST_PROBLEM, label: 'Intermittent Issue' },
    { value: PhoneReportOutcomeResolutionEnum.NETWORK_ISSUE, label: 'Network Problem' },
    { value: PhoneReportOutcomeResolutionEnum.NO_ISSUE, label: 'No Issue Found' },
    { value: PhoneReportOutcomeResolutionEnum.NUMBER_ISSUE, label: 'Number Issue' },
  ];

  readonly outcomeResolutionLabel = computed(() => {
    const report = this.phoneReport();
    const resolution = PhoneReportUtils.getReportOutcomeResolutionLabel(report.outcomeResolution) || 'Not specified';

    return resolution;
  });

  // controls
  readonly phoneReportOutcomeForm: FormGroup = this.formBuilder.group({
    outcomeResolution: [],
    outcomeDescription: ['', Validators.compose([Validators.maxLength(255)])],
  });

  private readonly resolutionBadgeClassMap: Record<string, string> = {
    [PhoneReportOutcomeResolutionEnum.UNDER_REVIEW]: 'badge-under-review',
    [PhoneReportOutcomeResolutionEnum.EXTERNAL_NUMBER]: 'badge-sender-issue',
    [PhoneReportOutcomeResolutionEnum.GHOST_PROBLEM]: 'badge-intermittent',
    [PhoneReportOutcomeResolutionEnum.NETWORK_ISSUE]: 'badge-network-problem',
    [PhoneReportOutcomeResolutionEnum.NO_ISSUE]: 'badge-no-issue',
    [PhoneReportOutcomeResolutionEnum.NUMBER_ISSUE]: 'badge-number-issue',
  };

  getResolutionBadgeClass(): string {
    const resolution = this.phoneReport()?.outcomeResolution;
    return resolution ? (this.resolutionBadgeClassMap[resolution] || 'badge-not-specified') : 'badge-not-specified';
  }

  onCloseClick(): void {
    this.dialogRef().close(false);
    this.modalClose.emit();
  }

  onOutcomeCommentSaveClick(): void {
    if (this.phoneReportOutcomeForm.invalid) {
      return;
    }

    const outcomeResolution = this.phoneReportOutcomeForm.get('outcomeResolution')?.value ?? null;
    const outcomeDescription = this.phoneReportOutcomeForm.get('outcomeDescription')?.value ?? null;
    const dto = {
      outcomeResolution,
      outcomeDescription: outcomeDescription ? outcomeDescription.trim() : null,
    };

    this.isReportOutcomeUpdating.set(true);
    this.phoneReportService
      .updateOutcomeResolution(this.phoneReport().id, dto)
      .pipe(
        catchError(e => {
          this.notificationService.error({
            title: 'Phone Report',
            message: 'An error occurred while updating report outcome',
            requestMessage: e.statusText,
            requestCode: e.status,
            ts: e.timestamp ? e.timestamp : null,
          });
          return throwError(() => e);
        }),
        finalize(() => this.isReportOutcomeUpdating.set(false))
      )
      .subscribe(() => {
        this.reportOutcomeUpdate.emit({
          id: this.phoneReport().id,
          outcomeResolution: dto.outcomeResolution,
          outcomeDescription: dto.outcomeDescription,
        });
        this.notificationService.success('Report outcome updated', 'Phone Report');
        this.dialogRef().close(false);
        this.modalClose.emit();
      });
  }

  onCancelClick(): void {
    const report = this.phoneReport();
    this.phoneReportOutcomeForm.reset({
      outcomeResolution: report?.outcomeResolution ?? null,
      outcomeDescription: report?.outcomeDescription ?? '',
    });
  }

  private readonly formSyncEffect = effect(() => {
    const report = this.phoneReport();
    const resolutionCtrl = this.phoneReportOutcomeForm.get('outcomeResolution');
    const descriptionCtrl = this.phoneReportOutcomeForm.get('outcomeDescription');

    if (!resolutionCtrl || !descriptionCtrl || !report) {
      return;
    }

    this.phoneReportOutcomeForm.reset(
      {
        outcomeResolution: report.outcomeResolution ?? null,
        outcomeDescription: report.outcomeDescription ?? '',
      },
      { emitEvent: false }
    );
  });
}
